import { useReducer, useEffect, useMemo, useState } from 'react';

const initialFilterState = {
  sort: 'lastContentUpdatedAt,DESC',
  includeGenreIds: [],
  excludeGenreIds: [],
  includeTagIds: [],
  excludeTagIds: [],
  minRating: '',
  maxRating: '',
  minRatingCount: '',
  maxRatingCount: '',
  minChapterCount: '',
  maxChapterCount: '',
  minViewCount: '',
  maxViewCount: '',
  isCompleted: 'any',
  genreInclusionMode: 'AND',
  tagInclusionMode: 'AND',
  originCountry: '',
  hideMature: false,
  excludeContentWarningIds: [],
};

const sanitizeFilterArrays = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => String(item)).filter((item) => item.length > 0);
};

const sanitizeNumericField = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return String(value);
};

export const sanitizeFilterState = (filtersPayload = {}) => {
  const merged = {
    ...initialFilterState,
    ...filtersPayload,
  };

  const sanitized = {
    ...merged,
    includeGenreIds: sanitizeFilterArrays(filtersPayload.includeGenreIds ?? merged.includeGenreIds),
    excludeGenreIds: sanitizeFilterArrays(filtersPayload.excludeGenreIds ?? merged.excludeGenreIds),
    includeTagIds: sanitizeFilterArrays(filtersPayload.includeTagIds ?? merged.includeTagIds),
    excludeTagIds: sanitizeFilterArrays(filtersPayload.excludeTagIds ?? merged.excludeTagIds),
    excludeContentWarningIds: sanitizeFilterArrays(filtersPayload.excludeContentWarningIds ?? merged.excludeContentWarningIds),
    minRating: sanitizeNumericField(filtersPayload.minRating ?? merged.minRating),
    maxRating: sanitizeNumericField(filtersPayload.maxRating ?? merged.maxRating),
    minRatingCount: sanitizeNumericField(filtersPayload.minRatingCount ?? merged.minRatingCount),
    maxRatingCount: sanitizeNumericField(filtersPayload.maxRatingCount ?? merged.maxRatingCount),
    minChapterCount: sanitizeNumericField(filtersPayload.minChapterCount ?? merged.minChapterCount),
    maxChapterCount: sanitizeNumericField(filtersPayload.maxChapterCount ?? merged.maxChapterCount),
    minViewCount: sanitizeNumericField(filtersPayload.minViewCount ?? merged.minViewCount),
    maxViewCount: sanitizeNumericField(filtersPayload.maxViewCount ?? merged.maxViewCount),
    genreInclusionMode: filtersPayload.genreInclusionMode ?? merged.genreInclusionMode,
    tagInclusionMode: filtersPayload.tagInclusionMode ?? merged.tagInclusionMode,
  };

  const validCompletionStates = new Set(['any', 'completed', 'ongoing']);
  if (!validCompletionStates.has(String(sanitized.isCompleted))) {
    sanitized.isCompleted = initialFilterState.isCompleted;
  }

  sanitized.hideMature = sanitized.hideMature === true || sanitized.hideMature === 'true';

  if (typeof sanitized.sort !== 'string' || sanitized.sort.length === 0) {
    sanitized.sort = initialFilterState.sort;
  }

  // Migrate legacy sort values: updatedAt → lastContentUpdatedAt
  if (sanitized.sort.startsWith('updatedAt,')) {
    sanitized.sort = sanitized.sort.replace('updatedAt,', 'lastContentUpdatedAt,');
  }

  return sanitized;
};

const sanitizeFiltersPayload = (payload = {}) => {
  const filtersSource = payload && typeof payload === 'object' && payload.filters
    ? payload.filters
    : payload;

  const sanitizedFilters = sanitizeFilterState(filtersSource);

  const pageCandidate = payload && typeof payload === 'object' && 'currentPage' in payload
    ? payload.currentPage
    : filtersSource.currentPage;

  const safePage = Number.isFinite(pageCandidate) ? Math.max(0, Math.floor(pageCandidate)) : 0;

  return {
    filters: sanitizedFilters,
    currentPage: safePage,
  };
};

const filtersReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        [action.field]: Array.isArray(action.value) ? [...action.value] : action.value,
      };
    case 'RESET':
      return {
        ...initialFilterState,
        includeGenreIds: [],
        excludeGenreIds: [],
        includeTagIds: [],
        excludeTagIds: [],
        excludeContentWarningIds: [],
      };
    case 'REPLACE':
      return {
        ...state,
        ...action.value,
        includeGenreIds: [...(action.value?.includeGenreIds ?? state.includeGenreIds ?? [])],
        excludeGenreIds: [...(action.value?.excludeGenreIds ?? state.excludeGenreIds ?? [])],
        includeTagIds: [...(action.value?.includeTagIds ?? state.includeTagIds ?? [])],
        excludeTagIds: [...(action.value?.excludeTagIds ?? state.excludeTagIds ?? [])],
        excludeContentWarningIds: [...(action.value?.excludeContentWarningIds ?? state.excludeContentWarningIds ?? [])],
      };
    default:
      return state;
  }
};

const countActiveFilters = (state) => {
  let count = 0;

  const arrayFields = ['includeGenreIds', 'excludeGenreIds', 'includeTagIds', 'excludeTagIds', 'excludeContentWarningIds'];
  arrayFields.forEach((field) => {
    if (state[field]?.length) {
      count += 1;
    }
  });

  const numericFields = [
    'minRating',
    'maxRating',
    'minRatingCount',
    'maxRatingCount',
    'minChapterCount',
    'maxChapterCount',
    'minViewCount',
    'maxViewCount',
  ];
  numericFields.forEach((field) => {
    const value = state[field];
    if (value !== '' && value !== null && value !== undefined) {
      count += 1;
    }
  });

  if (state.isCompleted && state.isCompleted !== 'any') {
    count += 1;
  }

  if (state.genreInclusionMode && state.genreInclusionMode !== 'AND' && state.includeGenreIds?.length > 0) {
    count += 1;
  }

  if (state.tagInclusionMode && state.tagInclusionMode !== 'AND' && state.includeTagIds?.length > 0) {
    count += 1;
  }

  if (state.sort && state.sort !== initialFilterState.sort) {
    count += 1;
  }

  if (state.originCountry && state.originCountry !== '') {
    count += 1;
  }

  if (state.hideMature) {
    count += 1;
  }

  return count;
};

/**
 * Hook to manage novel list filtering, pagination, and persistence.
 * @param {Object} options
 * @param {string} options.storageKey - LocalStorage key for persistence
 * @param {Function} [options.initInitializer] - Optional initializer for useReducer
 * @returns {Object} Filter state and handlers
 */
export const useNovelFilters = ({ storageKey, initInitializer, skipAutoHydration = false }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, dispatchFilters] = useReducer(
    filtersReducer,
    initialFilterState,
    initInitializer || undefined
  );
  
  // We need a separate draft state for the filter panel to avoid applying changes immediately
  const [draftFilters, dispatchDraftFilters] = useReducer(
    filtersReducer,
    initialFilterState,
    initInitializer || undefined
  );
  
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [pendingScrollToTop, setPendingScrollToTop] = useState(false);

  // Hydrate from local storage on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      setFiltersHydrated(true);
      return;
    }

    if (skipAutoHydration) {
      // If skipping auto-hydration (e.g. because URL params take precedence), 
      // we assume the consumer handles setting filtersHydrated to true when done.
      return;
    }

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const { filters: storedFilters, currentPage: storedPage } = sanitizeFiltersPayload(parsed);
        dispatchFilters({ type: 'REPLACE', value: storedFilters });
        dispatchDraftFilters({ type: 'REPLACE', value: storedFilters });
        setCurrentPage(storedPage);
      }
    } catch (storageError) {
      // eslint-disable-next-line no-console
      console.warn('Unable to hydrate library filters from storage:', storageError);
    } finally {
      setFiltersHydrated(true);
    }
  }, [storageKey, skipAutoHydration]);

  // Persist to local storage
  useEffect(() => {
    if (!filtersHydrated || typeof window === 'undefined' || !storageKey) {
      return;
    }

    try {
      const payload = {
        filters,
        currentPage,
      };
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (storageError) {
      // eslint-disable-next-line no-console
      console.warn('Unable to persist library filters to storage:', storageError);
    }
  }, [filters, filtersHydrated, currentPage, storageKey]);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);
  const hasActiveFilters = activeFilterCount > 0;

  return {
    filters,
    dispatchFilters,
    draftFilters,
    dispatchDraftFilters,
    currentPage,
    setCurrentPage,
    filtersHydrated,
    setFiltersHydrated,
    pendingScrollToTop,
    setPendingScrollToTop,
    activeFilterCount,
    hasActiveFilters,
    initialFilterState, // Export for comparison
    sanitizeFiltersPayload, // Export for manual hydration if needed
    filtersReducer, // Export if needed
  };
};
